import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Info, X } from 'lucide-react';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { MouseEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';
import { QueryKey } from '@/Config/api/queryKey';
import { ShopifyEditorLinkDto } from '@/Domain/api/Shopify/dto/ShopifyEditorLink.dto';
import { AiInvitation } from '@/Domain/components/Ai/AiInvitation';
import {
  OnboardingActionCards,
  PostOnboardingChecklist,
} from '@/Domain/components/OnboardingProgress';
import { PreviewContext } from '@/Domain/components/PreviewProvider';
import { Alert, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/Domain/components/Shared/Resizable/Resizable';
import {
  PostHogEventEnum,
  PostHogFeatureFlagEnum,
  PostHogUserPropertyEnum,
} from '@/Domain/enum/PostHogEvent.enum';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { cn } from '@/Domain/utils/cn';
import { useIsMobileWidth } from '@/Domain/utils/isMobile';
import { AdvancedSettings } from '@/UI/AiPreview/AdvancedSettings';
import {
  greetingFirstName,
  renderWelcomeTitle,
} from '@/UI/GettingStarted/components/AutomaticOnboarding';
import { PreviewSidePanel, SIDE_PANEL_CONTAINER } from '@/UI/Website/PreviewSidePanel';
import { WidgetPreviewPanel, type DeviceMode } from '@/UI/Website/WidgetPreviewPanel';
import { usePreviewFocusToggle } from '@/UI/Website/usePreviewFocusToggle';

const parsePreviewLanguage = (metadataStr: string): string | undefined => {
  try {
    const parsed = JSON.parse(metadataStr);
    return typeof parsed?.language === 'string' && parsed.language ? parsed.language : undefined;
  } catch {
    return undefined;
  }
};

type Props = {
  isActive?: boolean;
};

export const AiPreviewPage = ({ isActive = false }: Props) => {
  const postHog = usePostHog();
  const { t } = useTranslation('ai_preview_page');
  const { user } = useUser();
  // Width-only (matches the TopBar Preview button's md:hidden): a wide tablet keeps the panels.
  const isMobile = useIsMobileWidth();
  const {
    leftPanelRef,
    isFocused,
    isLeftPanelCollapsed,
    toggleFocus,
    collapseLeftPanel,
    expandLeftPanel,
    onLeftPanelCollapsedChange,
  } = usePreviewFocusToggle();

  // AI Preview only: desktop preview needs the full width, mobile hands the checklist back.
  const handleDeviceModeChange = useCallback(
    (mode: DeviceMode) => (mode === 'desktop' ? collapseLeftPanel() : expandLeftPanel()),
    [collapseLeftPanel, expandLeftPanel],
  );
  const greetingName = greetingFirstName(user?.first_name);
  const [newsVisible, setNewsVisible] = useState(false);
  const isNewsEnabled = useFeatureFlagEnabled(PostHogFeatureFlagEnum.AiPreviewNews);

  // Advanced Testing lets the user set a test `language`; reflect it in the preview
  // widget so the translated config loads. Debounced so typing in the metadata box
  // doesn't remount the widget on every keystroke.
  const previewMetadata = useContext(PreviewContext)?.metadata ?? '';
  const [previewLanguage, setPreviewLanguage] = useState<string | undefined>(() =>
    parsePreviewLanguage(previewMetadata),
  );
  const applyPreviewLanguage = useDebounceCallback(
    (metadataStr: string) => setPreviewLanguage(parsePreviewLanguage(metadataStr)),
    600,
  );
  useEffect(() => {
    applyPreviewLanguage(previewMetadata);
  }, [previewMetadata, applyPreviewLanguage]);

  const handleCloseNews = useCallback(
    (event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      postHog.capture(PostHogEventEnum.AiPreviewNewsClose);
      postHog.people.set({ [PostHogUserPropertyEnum.AiPreviewNews]: true });

      postHog.reloadFeatureFlags();
      setNewsVisible(false);
    },
    [postHog],
  );

  const { data: shopifyEditorLink } = useQuery<ShopifyEditorLinkDto>({
    queryKey: [QueryKey.shopify.editorLink],
    enabled: isActive,
  });

  const isShopifyUser = !!shopifyEditorLink?.editor_url;

  const handleOpenShopifyLink = () => {
    postHog.capture(PostHogEventEnum.ShopifyThemeEditorOpened, { source: 'ai_preview' });
    window.open(shopifyEditorLink?.editor_url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    setNewsVisible(!!isNewsEnabled);
  }, [isNewsEnabled]);

  const sideContent = (
    <div className="flex min-h-full flex-col justify-center gap-6">
      <div className="flex flex-col gap-3">
        <h1 className="font-safiro text-4xl font-medium text-foreground text-balance leading-[1.1]">
          {greetingName
            ? renderWelcomeTitle(t('welcome_with_name', { name: greetingName }), greetingName)
            : t('welcome')}
        </h1>
        <h2 className="text-base font-medium text-muted-foreground">{t('preview_description')}</h2>
      </div>

      {isShopifyUser && (
        <Button
          variant="secondary"
          size="md"
          onClick={handleOpenShopifyLink}
          className="w-full"
          data-testid="shopify-theme-editor-button"
        >
          <span className="min-w-0 truncate">
            <Trans
              t={t}
              i18nKey="button.enable_widget_shopify"
              components={{
                logo: (
                  <span className="inline">
                    <img
                      src="/static/assets/shopify-logo.webp"
                      alt="Shopify"
                      className="mx-1 inline h-5 w-auto align-[-0.25em] dark:hidden"
                    />
                    <img
                      src="/static/assets/shopify-logo-dark.webp"
                      alt="Shopify"
                      className="mx-1 hidden h-5 w-auto align-[-0.25em] dark:inline"
                    />
                  </span>
                ),
              }}
            />
          </span>
          <ArrowUpRight className="h-4 w-4" />
        </Button>
      )}

      <PostOnboardingChecklist isShopifyUser={isShopifyUser} />

      <div
        className="flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground"
        data-testid="ai-preview-or-divider"
      >
        <div className="h-px flex-1 bg-border" />
        <span>{t('or')}</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <OnboardingActionCards />
    </div>
  );

  const previewContent = (
    <div className="h-full w-full">
      <WidgetPreviewPanel
        isFocused={isFocused}
        isLeftPanelCollapsed={isLeftPanelCollapsed}
        onToggleFocus={toggleFocus}
        onDeviceModeChange={handleDeviceModeChange}
        advancedSettings={<AdvancedSettings />}
        language={previewLanguage}
        // Kept-alive host (hidden on other routes): must never own the Website live-edit iframe ref.
        bindContextIframe={false}
      />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="-m-6 flex flex-col h-[calc(100svh-var(--header-height,3.25rem))] overflow-hidden"
    >
      <div className="shrink-0 flex flex-col gap-3 empty:hidden [&:not(:empty)]:px-6 [&:not(:empty)]:py-4">
        <AiInvitation />

        <AnimatePresence>
          {newsVisible && (
            <motion.div
              key="preview-news-alert"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert variant="info">
                <AlertTitle
                  endIcon={
                    <Button
                      className="ml-auto !border-none"
                      variant="custom"
                      size="icon"
                      onClick={handleCloseNews}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  }
                >
                  <Info className="h-4 w-4" />
                  <span className="ml-2 text-sm">
                    <Trans
                      i18nKey={t('news.title')}
                      t={t}
                      components={{
                        analytics_link: (
                          <Link
                            className="font-semibold"
                            to="insights"
                            relative="path"
                            onClick={handleCloseNews}
                          />
                        ),
                      }}
                    />
                  </span>
                </AlertTitle>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {isMobile ? (
        <div
          className={cn('flex-1 min-h-0 w-full', SIDE_PANEL_CONTAINER)}
          style={{ scrollbarGutter: 'stable' }}
        >
          {sideContent}
        </div>
      ) : (
        // Deliberately not persisted: the device toggle resets to mobile on every load, so a saved
        // desktop-driven collapse would restore a hidden checklist under a mobile preview.
        <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
          <PreviewSidePanel panelRef={leftPanelRef} onCollapsedChange={onLeftPanelCollapsedChange}>
            {sideContent}
          </PreviewSidePanel>

          <ResizableHandle withHandle className="bg-border" />

          <ResizablePanel id="preview-main" order={2} minSize={40}>
            {previewContent}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </motion.div>
  );
};
